/**
 * PublicLiveTicker — publiczny strumień aktywności ekosystemu APISAI na landingu.
 *
 * Bez auth, bez PII. Polling /api/metrics/public-feed co 30s.
 * Pokazuje "Ktoś z Google obejrzał hodowlę 3 min temu" itp.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, Phone, Mail, MessageSquare, Send, Heart, Briefcase,
  Megaphone, Activity, MessageCircle, Star,
} from 'lucide-react';
import { API } from '@/utils/api';

const ICON_MAP = {
  eye: { Icon: Eye, color: 'text-violet-300', bg: 'bg-violet-500/15' },
  phone: { Icon: Phone, color: 'text-emerald-300', bg: 'bg-emerald-500/15' },
  whatsapp: { Icon: MessageCircle, color: 'text-emerald-300', bg: 'bg-emerald-500/15' },
  mail: { Icon: Mail, color: 'text-blue-300', bg: 'bg-blue-500/15' },
  form: { Icon: Send, color: 'text-fuchsia-300', bg: 'bg-fuchsia-500/15' },
  send: { Icon: Send, color: 'text-fuchsia-300', bg: 'bg-fuchsia-500/15' },
  heart: { Icon: Heart, color: 'text-pink-300', bg: 'bg-pink-500/15' },
  briefcase: { Icon: Briefcase, color: 'text-amber-300', bg: 'bg-amber-500/15' },
  megaphone: { Icon: Megaphone, color: 'text-orange-300', bg: 'bg-orange-500/15' },
  paw: { Icon: Heart, color: 'text-rose-300', bg: 'bg-rose-500/15' },
  message: { Icon: MessageSquare, color: 'text-blue-300', bg: 'bg-blue-500/15' },
  star: { Icon: Star, color: 'text-amber-300', bg: 'bg-amber-500/15' },
  activity: { Icon: Activity, color: 'text-zinc-300', bg: 'bg-zinc-500/15' },
};

const FALLBACK = [
  { id: 'fb1', icon: 'eye', title: 'Ktoś obejrzał hodowlę', source_label: 'z Google', relative_time: 'przed chwilą' },
  { id: 'fb2', icon: 'whatsapp', title: 'Ktoś wysłał wiadomość na WhatsApp', source_label: 'z katalogu APISAI', relative_time: '3 min temu' },
  { id: 'fb3', icon: 'paw', title: 'Ktoś obejrzał miot szczeniąt', source_label: 'z Facebooka', relative_time: '7 min temu' },
  { id: 'fb4', icon: 'briefcase', title: 'Ktoś sprawdził usługę lokalnego biznesu', source_label: 'z Map Google', relative_time: '12 min temu' },
];

export default function PublicLiveTicker({ limit = 6 }) {
  const [items, setItems] = useState(FALLBACK);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/metrics/public-feed?limit=${limit}`);
        const arr = res?.data?.items || [];
        if (mounted && arr.length > 0) {
          setItems(arr);
          setLive(true);
        }
      } catch {
        /* keep fallback */
      }
    };
    load();
    const int = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(int); };
  }, [limit]);

  return (
    <div className="relative" data-testid="public-live-ticker">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        <span className="text-xs uppercase tracking-[0.18em] text-emerald-300/90 font-semibold">
          {live ? 'Ekosystem APISAI · live' : 'Ekosystem APISAI'}
        </span>
      </div>
      <div className="space-y-1.5 max-h-[280px] overflow-hidden">
        <AnimatePresence initial={false}>
          {items.slice(0, limit).map((it) => {
            const meta = ICON_MAP[it.icon] || ICON_MAP.activity;
            const { Icon, color, bg } = meta;
            return (
              <motion.div
                key={it.id}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-orange-500/20 transition-colors"
              >
                <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-zinc-100 truncate">{it.title}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {it.source_label} · {it.relative_time}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
